<template>
    <!-- Transition between error state and content -->
    <transition name="fade">
        <div v-if="error.length">
            <InitError :error />
        </div>

        <div
            v-else
            key="content"
            v-qa="'app-content'"
            v-qa.has.cookie.banner="Boolean(!cookiesDismissedCookie)"
            v-qa.has.region.modal="showRegionModal"
            v-qa.suggested.region="data?.regionSuggest?.code"
        >
            <a
                href="#main"
                class="skip-link"
                tabindex="0"
                >Skip to main content</a
            >

            <PreHeader v-if="mainMenu" />

            <AppHeader
                v-if="mainMenu"
                :main-menu
            />

            <main
                id="main"
                v-qa="'main'"
                class="main"
            >
                <slot></slot>
            </main>

            <AppFooter
                v-if="footer"
                :data="footer"
                :payment-icons
                :social-networks
            />

            <IntercomLauncher />

            <EnquiryForm />

            <SpeakToExpertModal />

            <ContactUsForm />

            <CookieBanner
                :is-visible="!cookiesDismissedCookie"
                @dismiss="dismissCookiesBanner"
            />

            <RegionModal
                :show="showRegionModal"
                :suggested-region="data?.regionSuggest"
                @select-region="hasRegionSet = true"
            />

            <TooltipCmsViewer />

            <ConsultantWarning
                v-if="!hideGeneralWarningsCookie && isAdmin && user"
                :user
                @hide="hideConsultationWarning"
            />
        </div>
    </transition>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { useMenus } from '@/plugins/menu'
import { useInit } from './composables/app/useInit'
import { useResponseError } from '@/helpers/errors'
import { useAnalytics } from './composables/app/gtm/useAnalytics'
import {
    getRoutingRegion,
    isAppRegion,
    noPrefixRegion,
    useRegionPlugin,
} from '@/plugins/region'
import { makeCookie, useBooleanCookie } from '@/plugins/cookie-plugin'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useAuth } from '@/modules/auth'

import AppHeader from '@/components/layout/header/AppHeader.vue'
import AppFooter from '@/components/layout/footer/AppFooter.vue'
import EnquiryForm from './components/enquiry/EnquiryForm.vue'
import InitError from '@/components/InitError.vue'
import ContactUsForm from '@/components/enquiry/ContactUsForm.vue'
import SpeakToExpertModal from '@/components/app/speak-to-expert-modal/SpeakToExpertModal.vue'
import RegionModal from '@/components/RegionModal.vue'
import IntercomLauncher from './components/app/intercom/IntercomLauncher.vue'
import TooltipCmsViewer from './directives/tooltip/TooltipCmsViewer.vue'
import PreHeader from './components/layout/header/PreHeader.vue'
import CookieBanner from '@/components/CookieBanner.vue'
import { computed, defineAsyncComponent, hydrateOnIdle } from 'vue'
import {
    defineLocalBusiness,
    defineWebSite,
    useSchemaOrg,
} from '@unhead/schema-org/vue'
import type {
    OpeningHoursSpecification,
    PostalAddress,
} from '@unhead/schema-org'
import { useRoutes } from '@/router/routes.ts'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl.ts'
import type { Time } from '@/types/time.ts'
import type { AlternateTagFragment, RegionCode } from '@/graphql/api'
import { assertNever } from '@/helpers'
import { type Link, useHead } from '@unhead/vue'
import { useHost } from '@/plugins/host.ts'
import { useTranslator } from '@/i18n'

const ConsultantWarning = defineAsyncComponent({
    loader: () => import('@/components/ConsultantWarning.vue'),
    hydrate: hydrateOnIdle(),
})

const isAdmin = useIsAdmin()
const { user } = useAuth()
const { selectRegion, selectAndPersistRegion, hasRegionSet } = useRegionPlugin()
const routes = useRoutes()
const currentRoute = useRoute()
const absoluteUrl = useAbsoluteUrl()
const { regionCode } = useRegionPlugin()
const router = useRouter()
const host = useHost()
const { locale } = useTranslator()

// const router = useRouter()
// const { cookies: cookieStore } = storeToRefs(useCookiesStore())

// if (!import.meta.env.SSR) {
//     router.afterEach(() => {
//         const cookies = Cookie.get()
//
//         const cookiesInStore = cookieStore.value
//         for (const cookieInStore of Object.entries(cookiesInStore)) {
//             cookieStore.value[cookieInStore[0]] = cookies[cookieInStore[0]]
//
//             delete cookies[cookieInStore[0]]
//         }
//         for (const cookie of Object.entries(cookies)) {
//             cookieStore.value[cookie[0]] = cookie[1]
//         }
//     })
// }

const cookiesDismissedCookie = useBooleanCookie('cookies_dimissed')
const hideGeneralWarningsCookie = useBooleanCookie('hide_general_warnings')

const hideConsultationWarning = () => {
    hideGeneralWarningsCookie.value = makeCookie(true, {
        expires: new Date(new Date().getTime() + 15 * 60000),
    })
}

const dismissCookiesBanner = () => {
    cookiesDismissedCookie.value = true
}

const region: string | string[] =
    currentRoute.params['region'] || noPrefixRegion
if (typeof region === 'string') {
    const possiblyRegion = region.toUpperCase()

    if (isAppRegion(possiblyRegion)) {
        if (import.meta.env.SSR) {
            await selectRegion(possiblyRegion)
        } else {
            const hadRegionSet = hasRegionSet.value
            await selectAndPersistRegion(possiblyRegion).then(() => {
                // Reset hasRegionSet because any other call to selectAndPersistRegion
                // Will set it to true to prevent accidental display on explicit navigation to another region
                hasRegionSet.value = hadRegionSet
            })
        }
    }
}

const { initQueryResponse, showRegionModal } = await useInit(
    currentRoute,
    hasRegionSet
)

const { data } = initQueryResponse

const generateTag = (region: AlternateTagFragment, hreflang: string): Link => {
    // @ts-expect-error
    const route = router.resolve({
        ...router.currentRoute.value,
        params: {
            ...router.currentRoute.value.params,
            region: getRoutingRegion(region.code),
        },
    })

    return {
        rel: 'alternate',
        hreflang,
        href: absoluteUrl(route).href,
    }
}

const alternateTags = computed((): Link[] => {
    const tags: Link[] = []

    if (data.value?.defaultAlternateRegion) {
        tags.push(generateTag(data.value?.defaultAlternateRegion, 'x-default'))
    }

    tags.push(
        ...(data.value?.regions
            .map((region) =>
                region.locales.map((locale) => generateTag(region, locale))
            )
            .flat() || [])
    )

    return tags
})

const canonical = computed((): Link => {
    return {
        rel: 'canonical',
        href: absoluteUrl(
            // @ts-expect-error
            router.resolve({
                ...router.currentRoute.value,
                params: router.currentRoute.value.params,
                // removes Google Merchant Center id
                query: undefined,
            })
        ).href,
    }
})

useHead({
    link: computed(() => [canonical.value, ...alternateTags.value]),
    templateParams: {
        schemaOrg: {
            host: host.toString(),
            path: router.currentRoute.value.path,
            inLanguage: locale.value,
        },
    },
})

const name = 'Taylor & Hart' as const

const apiTimeToSchemaOrgTime = (
    time: Time
): `${number}${number}:${number}${number}` => {
    return `${Number(time[0] ?? 0)}${Number(time[1] ?? 0)}:${Number(time[3] ?? 0)}${Number(time[4] ?? 0)}`
}

const getAddress = (region: RegionCode): PostalAddress | undefined => {
    switch (region) {
        case 'UK':
            return {
                streetAddress: 'St. Clements House, 27 Clements Lane',
                postalCode: 'EC4N 7AE',
                addressCountry: 'GB',
                addressLocality: 'London',
            }
        case 'US':
            return {
                streetAddress: '55 5th Avenue',
                postalCode: 'NY 1003',
                addressCountry: 'US',
                addressLocality: 'New York',
            }
        case 'AU':
            return undefined
        case 'CA':
            return undefined
        case 'NZ':
            return undefined
        case 'INT':
            return undefined
        case 'EU':
            return undefined
        case 'IE':
            return undefined
        case 'ZA':
            return undefined
        default:
            return assertNever(region, undefined)
    }
}

useSchemaOrg([
    defineLocalBusiness({
        name,
        logo: absoluteUrl('/src/assets/taylor-and-hart-logo.svg').href,
        sameAs: [
            'https://www.instagram.com/taylorandhart',
            'https://www.pinterest.com/taylorandhart',
            'https://www.facebook.com/taylorandhartjeweller',
            'https://twitter.com/taylorandhart',
            'https://www.youtube.com/channel/UC2O2p4q7Zl4sqH6llNxUfbw',
        ],
        priceRange: '$$$',
        telephone: '+442031264915',
        email: 'enquiries@taylorandhart.com',
        openingHoursSpecification: computed((): OpeningHoursSpecification[] => {
            return (
                data.value?.workingHours.map((item) => {
                    return {
                        opens: apiTimeToSchemaOrgTime(item.opening),
                        closes: apiTimeToSchemaOrgTime(item.closing),
                        dayOfWeek: item.weekDays.map((item) => {
                            return item as
                                | 'Friday'
                                | 'Monday'
                                | 'PublicHolidays'
                                | 'Saturday'
                                | 'Sunday'
                                | 'Thursday'
                                | 'Tuesday'
                                | 'Wednesday'
                        }),
                    }
                }) || []
            )
        }),
        address: getAddress(regionCode.value),
    }),
    defineWebSite({
        url: absoluteUrl(routes.home()).href,
        name,
    }),
])

useAnalytics()

const { mainMenu, footer, paymentIcons, socialNetworks } = useMenus()

const error = useResponseError(initQueryResponse)
</script>

<style lang="scss" src="./styles/icon.scss"></style>
<style lang="scss" src="./styles/colors.scss"></style>
<style lang="scss" src="./styles/timing-functions.scss"></style>

// Unscoped styles. For elements outside the app tree (i.e. teleport targets)
<style lang="scss">
#teleport-tooltip {
    position: fixed;
    z-index: 3000;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

//#teleport-cookies-banner {
.cookies-banner {
    position: fixed;
    bottom: 15px;
    left: 15px;
    padding: 15px 15px 10px;
    max-width: 220px;
    z-index: 50;
    background-color: $stone-2;
    box-shadow: 0 0 15px rgba($black, 0.2);

    .message {
        margin: 0 0 10px;
        font-size: 0.875rem;
        line-height: 1.2em;

        a {
            text-decoration: underline;
        }
    }

    .cookie-button-wrapper {
        width: 100%;
        display: flex;
        justify-content: flex-end;

        .dismiss-button {
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.75rem;
            text-transform: uppercase;
            font-weight: 400;
            color: $blue;
            cursor: pointer;
        }
    }
}
</style>

<style lang="scss" scoped>
.skip-link {
    position: absolute;
    top: 280px;
    left: 40px;
    z-index: 1000;
    width: 120px;
    padding: 12px;
    background-color: $stone-2;

    font-family: $effra-font-stack;
    letter-spacing: 0.05em;
    font-size: 1rem;
    font-weight: 400;
    color: $blue;
    cursor: pointer;
    text-align: center;
    text-decoration: underline;

    &:not(:focus) {
        clip: rect(1px, 1px, 1px, 1px);
        overflow: hidden;
        top: -1px;
        left: -1px;
        padding: 0;
    }
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.45s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
